import { useCallback, useState } from "react";
import Papa from "papaparse";
import { Button, Card } from "../../components/ui";
import { getAuthHeaders } from "../../data/api";

// CsvUploader — modal reusable para los uploads bulk (M9). Cada caller
// configura las columnas esperadas + un transformer que produce el
// shape JSON que el backend acepta, y un endpoint.
//
// Diseño:
//   - Parser tolerante: case-insensitive headers + alias por campo.
//   - Validación se hace en el backend; acá solo "¿esta columna
//     existe?". La validación fina (email vacío, etc.) viene como
//     errors[] en la respuesta.
//   - Después del upload mostramos el resumen {created, updated,
//     errors[]} con un colapsable para las filas que fallaron.

export type CsvColumn = {
  /** Clave de salida — coincide con el campo del JSON al backend. */
  key: string;
  /** Aliases que aceptamos en el CSV (case-insensitive, trimmed). */
  aliases: string[];
  required?: boolean;
  /** Tipo de transformación; default es string. */
  type?: "string" | "number" | "numberOptional";
};

export type CsvUploadSummary = {
  created?: number;
  updated?: number;
  matched?: number;
  errors?: { row_index: number; code?: string; reason: string }[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Texto auxiliar bajo el título (qué columnas espera, ejemplo). */
  description?: React.ReactNode;
  columns: CsvColumn[];
  /** URL del endpoint backend. */
  endpoint: string;
  /** Setear si querés que se ejecute algo en la app tras success
   *  (típicamente refrescar la lista mostrada en la página padre). */
  onSuccess?: (summary: CsvUploadSummary) => void;
};

export function CsvUploader(props: Props) {
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<CsvUploadSummary | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const onFile = useCallback(
    async (file: File) => {
      setError(null);
      setSummary(null);
      setFileName(file.name);
      setParsing(true);
      try {
        const text = await file.text();
        const { rows, missingRequired } = parseAndMap(text, props.columns);
        if (missingRequired.length > 0) {
          setError(`Faltan columnas requeridas: ${missingRequired.join(", ")}`);
          return;
        }
        if (rows.length === 0) {
          setError("El archivo no tiene filas de datos");
          return;
        }
        setParsing(false);
        setUploading(true);
        const res = await fetch(props.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ rows }),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`API ${res.status}: ${txt || res.statusText}`);
        }
        const data = (await res.json()) as CsvUploadSummary;
        setSummary(data);
        props.onSuccess?.(data);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setParsing(false);
        setUploading(false);
      }
    },
    [props],
  );

  if (!props.open) return null;

  const busy = parsing || uploading;
  const successCount = (summary?.created ?? 0) + (summary?.updated ?? 0) + (summary?.matched ?? 0);
  const errorCount = summary?.errors?.length ?? 0;

  return (
    <div
      className="fixed inset-0 z-[2000] bg-ink/50 flex items-center justify-center p-4"
      onClick={props.onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg">
        <Card padding="lg">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-border">
            <h2 className="text-base font-semibold text-ink">{props.title}</h2>
            <button onClick={props.onClose} className="text-sub hover:text-ink text-xs">
              cerrar ✕
            </button>
          </div>

          {props.description && (
            <div className="text-xs text-sub mb-4">{props.description}</div>
          )}

          <label
            className={`block border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition-colors ${
              busy ? "opacity-60 pointer-events-none" : "border-border hover:border-primary hover:bg-primary/5"
            }`}
          >
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
              }}
              className="hidden"
            />
            <div className="text-2xl mb-1">📄</div>
            <div className="text-sm text-ink font-medium">
              {parsing ? "Leyendo CSV…" : uploading ? "Subiendo…" : "Click para seleccionar CSV"}
            </div>
            {fileName && !busy && (
              <div className="text-xs text-sub mt-1">Último: {fileName}</div>
            )}
          </label>

          {error && (
            <div className="mt-3 p-3 rounded bg-state-failed/10 border border-state-failed text-state-failed text-sm">
              {error}
            </div>
          )}

          {summary && (
            <div className="mt-3 space-y-2">
              <div className="grid grid-cols-3 gap-2 text-center">
                {summary.created !== undefined && (
                  <KpiBox label="Creadas" value={summary.created} accent="delivered" />
                )}
                {summary.updated !== undefined && (
                  <KpiBox label="Actualizadas" value={summary.updated} />
                )}
                {summary.matched !== undefined && (
                  <KpiBox label="Vinculadas" value={summary.matched} accent="delivered" />
                )}
                <KpiBox label="Errores" value={errorCount} accent={errorCount > 0 ? "failed" : undefined} />
              </div>
              <div className="text-xs text-sub text-center">
                Total procesado: {successCount + errorCount} fila{successCount + errorCount === 1 ? "" : "s"}
              </div>
              {errorCount > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-state-failed">
                    Ver {errorCount} error{errorCount === 1 ? "" : "es"}
                  </summary>
                  <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {summary.errors!.map((er, i) => (
                      <li key={i} className="text-sub">
                        <strong>fila {er.row_index}</strong>
                        {er.code ? ` (${er.code})` : ""}: {er.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          <div className="flex justify-end mt-4 gap-2">
            <Button variant="ghost" size="sm" onClick={props.onClose}>
              {summary ? "Cerrar" : "Cancelar"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function KpiBox({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "delivered" | "failed";
}) {
  const color =
    accent === "delivered"
      ? "text-state-delivered"
      : accent === "failed"
        ? "text-state-failed"
        : "text-ink";
  return (
    <div className="bg-page rounded p-2">
      <div className={`text-xl font-semibold ${color}`}>{value}</div>
      <div className="text-[10px] text-sub uppercase tracking-wide">{label}</div>
    </div>
  );
}

// --------------- parser ---------------

/**
 * Parsea CSV y mapea cada fila al shape esperado. Devuelve:
 *   - rows: filas válidas (al menos una columna mapeada)
 *   - missingRequired: keys de columnas marcadas required que no se
 *     encontraron entre los headers del archivo.
 */
function parseAndMap(
  text: string,
  columns: CsvColumn[],
): { rows: Record<string, unknown>[]; missingRequired: string[] } {
  // Skip leading blank lines (típico de Numbers/Excel)
  const cleaned = text.replace(/^(\s*\r?\n)+/, "");
  const parsed = Papa.parse<string[]>(cleaned, { skipEmptyLines: "greedy" });

  // Detectar la fila de header en las primeras 5 filas.
  let headerIdx = -1;
  let headerMap: (CsvColumn | null)[] = [];
  for (let i = 0; i < Math.min(5, parsed.data.length); i++) {
    const row = parsed.data[i] ?? [];
    const map = row.map((cell) => matchColumn(cell, columns));
    if (map.filter((x) => x !== null).length >= Math.max(1, requiredCount(columns))) {
      headerIdx = i;
      headerMap = map;
      break;
    }
  }
  if (headerIdx < 0) {
    return {
      rows: [],
      missingRequired: columns.filter((c) => c.required).map((c) => c.aliases[0] ?? c.key),
    };
  }

  const presentKeys = new Set(headerMap.filter((c): c is CsvColumn => c !== null).map((c) => c.key));
  const missingRequired = columns
    .filter((c) => c.required && !presentKeys.has(c.key))
    .map((c) => c.aliases[0] ?? c.key);
  if (missingRequired.length > 0) {
    return { rows: [], missingRequired };
  }

  const rows: Record<string, unknown>[] = [];
  for (let i = headerIdx + 1; i < parsed.data.length; i++) {
    const cells = parsed.data[i] ?? [];
    const out: Record<string, unknown> = {};
    let hasAny = false;
    for (let c = 0; c < headerMap.length; c++) {
      const col = headerMap[c];
      if (!col) continue;
      const raw = (cells[c] ?? "").toString().trim();
      if (raw === "") {
        // Para campos opcionales numéricos, mandamos null (no NaN).
        if (col.type === "numberOptional") out[col.key] = null;
        continue;
      }
      hasAny = true;
      switch (col.type) {
        case "number":
        case "numberOptional": {
          const n = parseFloat(raw.replace(",", "."));
          out[col.key] = Number.isFinite(n) ? n : null;
          break;
        }
        default:
          out[col.key] = raw;
      }
    }
    if (hasAny) rows.push(out);
  }
  return { rows, missingRequired: [] };
}

function matchColumn(cell: string, columns: CsvColumn[]): CsvColumn | null {
  const norm = cell.trim().toLowerCase();
  for (const col of columns) {
    if (col.aliases.some((a) => a.toLowerCase() === norm)) return col;
  }
  return null;
}

function requiredCount(columns: CsvColumn[]): number {
  return columns.filter((c) => c.required).length;
}
